import { useEffect, useRef } from 'react';

import {
  useAccountContext,
  useCanSberIdLink,
  useCompanyId,
  useSberIdWasLinked,
} from 'application/providers/AccountProvider';
import { usePaymentMethodRepositoryInjector } from 'application/providers/DIContainerProvider';
import { RouterUtils } from 'application/providers/RouterProvider';
import AnalyticsService, { EventNameAMP } from 'application/services/AnalyticsService';
import { BILLING_PAGE_PATH } from 'application/consts';
import { StateParamValues } from 'application/pages/BillingPage';
import { NetworkStatus, ObservableQuery, useApolloClient } from 'application/providers/DataProvider';

import {
  BillingPaymentMethodDocument,
  BillingPaymentMethodQuery,
  PaymentMethodObjectPureType,
  useBillingCompanyBillingInfoQuery,
} from 'domain/api/graphql/generated';
import { FetchPolicyKeys } from 'domain/api/graphql/types';

import { UseCardInformationViewModel } from './types';
import { DEFAULT_INTERVAL, MAX_PAYMENT_METHOD_REQUEST_ATTEMPTS } from './consts';

interface Props {
  setPaymentMethod: (paymentMethod: PaymentMethodObjectPureType | null) => void;
  setIsLinkingInProcess: (isLinkingInProcess: boolean) => void;
  setPaymentMethodLoading: (paymentMethodLoading: boolean) => void;
  openBillingBindCardModal: () => void;
  openBillingRemoveCardModal: (afterClose: () => void) => void;
  showCounterNotification: (count: number) => void;
  showCounterErrorNotification: () => void;
  showBindErrorNotification: () => void;
}

const useCardInformationViewModel = ({
  setPaymentMethod,
  setIsLinkingInProcess,
  setPaymentMethodLoading,
  openBillingBindCardModal,
  openBillingRemoveCardModal,
  showCounterNotification,
  showCounterErrorNotification,
  showBindErrorNotification,
}: Props): UseCardInformationViewModel => {
  const client = useApolloClient();
  const companyId = useCompanyId();
  const canSberIdLink = useCanSberIdLink();
  const sberIdWasLinked = useSberIdWasLinked();
  const { userLoading } = useAccountContext();

  const { getLinkForBindingCard, bindCardLoading, removeCardLoading } = usePaymentMethodRepositoryInjector();

  const { data, loading } = useBillingCompanyBillingInfoQuery({
    variables: {
      companyId,
    },
    fetchPolicy: FetchPolicyKeys.CACHE_AND_NETWORK,
  });

  const countAttemptsRef = useRef<number>(0);
  const observableQueryRef = useRef<ObservableQuery<BillingPaymentMethodQuery>>();

  useEffect(() => {
    countAttemptsRef.current = 0;

    const observableQuery = client.watchQuery<BillingPaymentMethodQuery>({
      query: BillingPaymentMethodDocument,
      variables: { companyId },
      fetchPolicy: FetchPolicyKeys.NETWORK_ONLY,
      notifyOnNetworkStatusChange: true,
    });

    const subscription = observableQuery.subscribe({
      next: (result) => {
        const paymentMethod = result?.data?.paymentMethodPureQuery?.paymentMethod || null;
        const isLinkingInProcess = Boolean(result?.data?.paymentMethodPureQuery?.isLinkingInProcess);
        const paymentMethodLoading = Boolean(result?.loading);

        setPaymentMethod(paymentMethod);
        setIsLinkingInProcess(isLinkingInProcess);
        setPaymentMethodLoading(paymentMethodLoading);

        if (paymentMethod || (!paymentMethod && !isLinkingInProcess)) {
          observableQuery.stopPolling();
          subscription.unsubscribe();
          return;
        }

        if (result.networkStatus === NetworkStatus.poll) {
          countAttemptsRef.current += 1;
          showCounterNotification(countAttemptsRef.current);

          if (countAttemptsRef.current >= MAX_PAYMENT_METHOD_REQUEST_ATTEMPTS) {
            observableQuery.stopPolling();
            subscription.unsubscribe();
            showCounterErrorNotification();
          }
        }
      },
      error: (error) => {
        AnalyticsService.event(EventNameAMP.BIND_CARD_ERROR, { error });
      },
    });

    observableQuery.startPolling(DEFAULT_INTERVAL);
    observableQueryRef.current = observableQuery;

    return () => {
      subscription.unsubscribe();
      observableQuery.stopPolling();
    };
  }, [client, companyId]);

  const onBindCardClick = async () => {
    AnalyticsService.event(EventNameAMP.BIND_CARD_PRESSED);

    const pathname = RouterUtils.generatePath(BILLING_PAGE_PATH, { state: StateParamValues.INFO });

    const link = await getLinkForBindingCard(`${window.location.origin}/${pathname}`, companyId);

    if (link) {
      window.location.assign(link);
    } else {
      showBindErrorNotification();
    }
  };

  const onRebindCardClick = () => {
    AnalyticsService.event(EventNameAMP.BIND_CARD_PRESSED);

    openBillingBindCardModal();
  };

  const onRemoveCardClick = () => {
    openBillingRemoveCardModal(() => {
      setPaymentMethod(null);
      setIsLinkingInProcess(false);
      setPaymentMethodLoading(false);
    });
  };

  const sberAuthButtonVisible =
    (canSberIdLink && !userLoading && Boolean(data?.result?.hasInactiveLicenses)) || sberIdWasLinked;

  return {
    cardActionsLoading: bindCardLoading || removeCardLoading || loading,
    hasWaitPaymentLicenses: Boolean(data?.result?.hasWaitPaymentLicenses || data?.result?.isWaitPaymentLicense),
    hasTrialLicenses: Boolean(data?.result?.hasTrialLicenses),
    canSberIdLink,
    sberIdWasLinked,
    sberAuthButtonVisible,
    onBindCardClick,
    onRebindCardClick,
    onRemoveCardClick,
  };
};

export default useCardInformationViewModel;
